import React from "react";
import { alpha } from "@mui/material/styles";
import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import { formatTime, parseAgendaDate } from "./agendaDateUtils";

const DAY_START_HOUR = 6;
const BASE_DAY_END_HOUR = 22;
const HOUR_HEIGHT = 72;

const getMinutesOfDay = (value) => {
  const date = parseAgendaDate(value);

  if (!date) return null;

  return date.getHours() * 60 + date.getMinutes();
};

const buildLayoutItems = (items, dayEndHour) => {
  const dayStartMinutes = DAY_START_HOUR * 60;
  const dayEndMinutes = dayEndHour * 60;

  const normalized = (items || [])
    .map((item) => {
      const rawStart = getMinutesOfDay(item.starts_at);
      const rawEnd = getMinutesOfDay(item.ends_at);

      if (rawStart === null || rawEnd === null || rawEnd <= rawStart) {
        return null;
      }

      if (rawEnd <= dayStartMinutes || rawStart >= dayEndMinutes) {
        return null;
      }

      return {
        ...item,
        layoutStart: Math.max(rawStart, dayStartMinutes),
        layoutEnd: Math.min(rawEnd, dayEndMinutes),
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (a.layoutStart === b.layoutStart) {
        return a.layoutEnd - b.layoutEnd;
      }
      return a.layoutStart - b.layoutStart;
    });

  const active = [];

  const withLanes = normalized.map((item) => {
    for (let index = active.length - 1; index >= 0; index -= 1) {
      if (active[index].layoutEnd <= item.layoutStart) {
        active.splice(index, 1);
      }
    }

    const usedLanes = new Set(active.map((activeItem) => activeItem.lane));
    let lane = 0;

    while (usedLanes.has(lane)) {
      lane += 1;
    }

    const next = { ...item, lane };
    active.push(next);
    return next;
  });

  return withLanes.map((item) => {
    const overlaps = withLanes.filter(
      (other) => other.layoutStart < item.layoutEnd && other.layoutEnd > item.layoutStart
    );

    return {
      ...item,
      columns: Math.max(1, ...overlaps.map((other) => other.lane + 1)),
    };
  });
};

export default function AgendaCalendar({
  items,
  resolveItemColor,
  onSelectItem,
}) {
  const computedDayEndHour = React.useMemo(() => {
    const latestHour = Math.max(
      BASE_DAY_END_HOUR,
      ...(items || [])
        .map((item) => {
          const endsAt = parseAgendaDate(item?.ends_at);
          if (!endsAt) return BASE_DAY_END_HOUR;
          const roundedHour = endsAt.getMinutes() > 0 ? endsAt.getHours() + 1 : endsAt.getHours();
          return Math.min(24, Math.max(DAY_START_HOUR + 1, roundedHour));
        })
    );

    return latestHour;
  }, [items]);

  const totalHeight = (computedDayEndHour - DAY_START_HOUR) * HOUR_HEIGHT;
  const layoutItems = React.useMemo(
    () => buildLayoutItems(items, computedDayEndHour),
    [items, computedDayEndHour]
  );

  return (
    <Card sx={{ borderRadius: 4 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Timeline
        </Typography>

        {(items || []).length === 0 ? (
          <Typography color="text.secondary">
            No agenda items scheduled for the selected day.
          </Typography>
        ) : (
          <Stack
            direction="row"
            spacing={2}
            sx={{ overflowX: "auto", overflowY: "auto", maxHeight: "75vh", pr: 1 }}
          >
            <Box sx={{ minWidth: 64 }}>
              {Array.from({ length: computedDayEndHour - DAY_START_HOUR + 1 }).map((_, index) => {
                const hour = DAY_START_HOUR + index;
                const hourLabel = `${String(hour).padStart(2, "0")}:00`;

                return (
                  <Box
                    key={hourLabel}
                    sx={{
                      height: HOUR_HEIGHT,
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "flex-start",
                      pt: 0.5,
                      pr: 1,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {hourLabel}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            <Box
              sx={{
                position: "relative",
                flex: 1,
                minWidth: { xs: 260, md: 520 },
                height: totalHeight,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                background:
                  "linear-gradient(180deg, rgba(248,250,252,0.96) 0%, rgba(241,245,249,0.96) 100%)",
                overflow: "hidden",
              }}
            >
              {Array.from({ length: computedDayEndHour - DAY_START_HOUR }).map((_, index) => (
                <Box
                  key={`hour-line-${index}`}
                  sx={{
                    position: "absolute",
                    top: index * HOUR_HEIGHT,
                    left: 0,
                    right: 0,
                    height: HOUR_HEIGHT,
                    borderTop: index === 0 ? "none" : "1px solid rgba(148,163,184,0.18)",
                  }}
                />
              ))}

              {layoutItems.map((item) => {
                const color = resolveItemColor(item);
                const top = ((item.layoutStart - (DAY_START_HOUR * 60)) / 60) * HOUR_HEIGHT;
                const height = Math.max(
                  50,
                  ((item.layoutEnd - item.layoutStart) / 60) * HOUR_HEIGHT
                );
                const leftPercent = (item.lane / item.columns) * 100;
                const widthPercent = 100 / item.columns;

                return (
                  <Box
                    key={item.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelectItem?.(item)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onSelectItem?.(item);
                      }
                    }}
                    sx={{
                      position: "absolute",
                      top,
                      left: `calc(${leftPercent}% + 8px)`,
                      width: `calc(${widthPercent}% - 16px)`,
                      minWidth: item.columns > 1 ? 120 : "auto",
                      height,
                      p: 1.25,
                      borderRadius: 3,
                      cursor: "pointer",
                      overflow: "hidden",
                      border: `1px solid ${alpha(color, 0.28)}`,
                      background: `linear-gradient(135deg, ${alpha(color, 0.22)} 0%, ${alpha(color, 0.1)} 100%)`,
                      boxShadow: `0 10px 20px ${alpha(color, 0.16)}, inset 4px 0 0 ${color}`,
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
                      {formatTime(item.starts_at)} - {formatTime(item.ends_at)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        fontWeight: 700,
                        lineHeight: 1.2,
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 2,
                        overflow: "hidden",
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        mt: 0.5,
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 2,
                        overflow: "hidden",
                      }}
                    >
                      {item.location || item.subtitle || item.source}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
